import { supabase } from '../lib/supabase';

/**
 * Service for managing global leaderboards
 * Supports tiered competition (Global → Regional → Team), multiple metrics, and seasonal resets
 */

// Leaderboard tier types
export const LEADERBOARD_TIERS = {
  GLOBAL: 'global',
  REGIONAL: 'regional',
  TEAM: 'team',
  INDIVIDUAL: 'individual'
};

// Leaderboard metric filters
export const LEADERBOARD_METRICS = {
  TOTAL_XP: 'total_xp',
  OBJECTION_MASTERY: 'objections_mastered',
  CONSISTENCY_STREAK: 'longest_streak',
  PERFECT_SCORES: 'perfect_scores',
  IMPROVEMENT_RATE: 'improvement_rate'
};

/**
 * Get active season
 * @returns {Object} - Active season data
 */
export const getActiveSeason = async () => {
  const { data, error } = await supabase
    .from('leaderboard_seasons')
    .select('*')
    .eq('is_active', true)
    .single();

  if (error) {
    console.error('Error fetching active season:', error);
    return null;
  }

  return data;
};

/**
 * Get leaderboard by tier and metric
 * @param {string} tier - Leaderboard tier (global, regional, team)
 * @param {string} metric - Metric to sort by (total_xp, objections_mastered, etc.)
 * @param {Object} options - Filter options (region, teamId, limit)
 * @returns {Array} - Leaderboard entries with user profiles
 */
export const getLeaderboard = async (tier = LEADERBOARD_TIERS.GLOBAL, metric = LEADERBOARD_METRICS.TOTAL_XP, options = {}) => {
  const { region, teamId, limit = 100 } = options;

  // Get active season
  const season = await getActiveSeason();
  if (!season) {
    return [];
  }

  let query = supabase
    .from('user_leaderboard_stats')
    .select(`
      *,
      user_profiles!inner (
        id,
        full_name,
        avatar_url,
        company_name
      )
    `)
    .eq('season_id', season.id)
    .order(metric, { ascending: false })
    .limit(limit);

  // Apply tier filters
  if (tier === LEADERBOARD_TIERS.REGIONAL && region) {
    query = query.eq('region', region);
  } else if (tier === LEADERBOARD_TIERS.TEAM && teamId) {
    query = query.eq('team_id', teamId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }

  // Add rank based on filtered results
  return data?.map((entry, index) => ({
    ...entry,
    display_rank: index + 1,
    user: entry.user_profiles
  })) || [];
};

/**
 * Get user's leaderboard stats for active season
 * @param {string} userId - User ID
 * @returns {Object} - User's stats and ranks
 */
export const getUserLeaderboardStats = async (userId) => {
  const season = await getActiveSeason();
  if (!season) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_leaderboard_stats')
    .select('*')
    .eq('user_id', userId)
    .eq('season_id', season.id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user leaderboard stats:', error);
    return null;
  }

  return data;
};

/**
 * Get user's rank history (snapshots over time)
 * @param {string} userId - User ID
 * @param {number} days - Number of days to fetch (default 30)
 * @returns {Array} - Daily snapshots
 */
export const getUserRankHistory = async (userId, days = 30) => {
  const season = await getActiveSeason();
  if (!season) {
    return [];
  }

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data, error } = await supabase
    .from('leaderboard_snapshots')
    .select('*')
    .eq('user_id', userId)
    .eq('season_id', season.id)
    .gte('snapshot_date', startDate.toISOString().split('T')[0])
    .order('snapshot_date', { ascending: true });

  if (error) {
    console.error('Error fetching rank history:', error);
    return [];
  }

  return data || [];
};

/**
 * Get user's achievements for active season
 * @param {string} userId - User ID
 * @returns {Array} - Achievement badges
 */
export const getUserAchievements = async (userId) => {
  const season = await getActiveSeason();
  if (!season) {
    return [];
  }

  const { data, error } = await supabase
    .from('leaderboard_achievements')
    .select('*')
    .eq('user_id', userId)
    .eq('season_id', season.id)
    .order('awarded_at', { ascending: false });

  if (error) {
    console.error('Error fetching achievements:', error);
    return [];
  }

  return data || [];
};

/**
 * Get top achievers by metric (for badges/highlights)
 * @param {string} metric - Metric to check (objections_mastered, longest_streak, etc.)
 * @param {number} threshold - Minimum value to qualify
 * @returns {Array} - Top users
 */
export const getTopAchievers = async (metric, threshold = 10) => {
  const season = await getActiveSeason();
  if (!season) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_leaderboard_stats')
    .select(`
      *,
      user_profiles!inner (
        id,
        full_name,
        avatar_url
      )
    `)
    .eq('season_id', season.id)
    .gte(metric, threshold)
    .order(metric, { ascending: false })
    .limit(10);

  if (error) {
    console.error('Error fetching top achievers:', error);
    return [];
  }

  return data?.map(entry => ({
    ...entry,
    user: entry.user_profiles
  })) || [];
};

/**
 * Get leaderboard comparison (user vs. top performers)
 * @param {string} userId - User ID
 * @param {string} metric - Metric to compare
 * @returns {Object} - Comparison data
 */
export const getLeaderboardComparison = async (userId, metric = LEADERBOARD_METRICS.TOTAL_XP) => {
  const season = await getActiveSeason();
  if (!season) {
    return null;
  }

  // Get user's stats
  const userStats = await getUserLeaderboardStats(userId);
  if (!userStats) {
    return null;
  }

  // Get top 3 in each tier
  const globalTop3 = await getLeaderboard(LEADERBOARD_TIERS.GLOBAL, metric, { limit: 3 });
  const regionalTop3 = userStats.region 
    ? await getLeaderboard(LEADERBOARD_TIERS.REGIONAL, metric, { region: userStats.region, limit: 3 })
    : [];
  const teamTop3 = userStats.team_id
    ? await getLeaderboard(LEADERBOARD_TIERS.TEAM, metric, { teamId: userStats.team_id, limit: 3 })
    : [];

  return {
    user: userStats,
    global: {
      top3: globalTop3,
      userRank: userStats.global_rank,
      total: globalTop3[0]?.display_rank || 0
    },
    regional: {
      top3: regionalTop3,
      userRank: userStats.regional_rank,
      region: userStats.region
    },
    team: {
      top3: teamTop3,
      userRank: userStats.team_rank,
      teamId: userStats.team_id
    }
  };
};

/**
 * Update user leaderboard stats (called after drill/objection completion)
 * @param {string} userId - User ID
 * @param {string} activityType - 'drill', 'objection', 'training'
 * @param {number} xpEarned - XP awarded
 * @param {number} score - Activity score (0-100)
 * @param {number} difficulty - Difficulty level (1-5)
 * @param {boolean} isPerfect - Whether score was 100%
 */
export const updateLeaderboardStats = async (userId, activityType, xpEarned, score, difficulty = 1, isPerfect = false) => {
  const { error } = await supabase.rpc('update_leaderboard_stats', {
    p_user_id: userId,
    p_activity_type: activityType,
    p_xp_earned: xpEarned,
    p_score: score,
    p_difficulty: difficulty,
    p_is_perfect: isPerfect
  });

  if (error) {
    console.error('Error updating leaderboard stats:', error);
    return false;
  }

  return true;
};

/**
 * Calculate rankings (admin/cron function)
 * Should be called nightly via edge function or cron job
 */
export const calculateRankings = async () => {
  const { error } = await supabase.rpc('calculate_leaderboard_rankings');

  if (error) {
    console.error('Error calculating rankings:', error);
    return false;
  }

  return true;
};

/**
 * Get all seasons (for season selector)
 * @returns {Array} - All seasons
 */
export const getAllSeasons = async () => {
  const { data, error } = await supabase
    .from('leaderboard_seasons')
    .select('*')
    .order('start_date', { ascending: false });

  if (error) {
    console.error('Error fetching seasons:', error);
    return [];
  }

  return data || [];
};

/**
 * Get leaderboard stats summary (for dashboard)
 * @param {string} userId - User ID
 * @returns {Object} - Summary stats
 */
export const getLeaderboardSummary = async (userId) => {
  const stats = await getUserLeaderboardStats(userId);
  if (!stats) {
    return {
      totalXp: 0,
      globalRank: null,
      regionalRank: null,
      teamRank: null,
      streak: 0,
      achievements: []
    };
  }

  const achievements = await getUserAchievements(userId);

  return {
    totalXp: stats.total_xp || 0,
    globalRank: stats.global_rank,
    regionalRank: stats.regional_rank,
    teamRank: stats.team_rank,
    streak: stats.longest_streak || 0,
    perfectScores: stats.perfect_scores || 0,
    objectionsMastered: stats.objections_mastered || 0,
    achievements: achievements.slice(0, 5) // Latest 5 achievements
  };
};
